// „Как ще работи" — живият пример в страничната колона на редактора. Вместо
// статичен текст смята от ТЕКУЩИТЕ карти какво ще предложи системата:
// „Липсват 10 кг Кафе A → 1. 7 кг Кафе B, 2. 12 кг Кафе C". Кой продукт липсва
// се избира с чиповете отгоре. Над примера е готовността за запис (поне 2
// продукта, количество навсякъде); без продукти — пример с измислени кафета,
// който обяснява какво е правило.
//
// Числата са същите като на машината за замени: донор = липса × количество
// на донора / количество на получателя (`previewSteps`), подредени по
// приоритета, който ЩЕ се запише.
import { useState } from "react";
import { CheckCircleFilled, ExclamationCircleFilled } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { KbdChip } from "../../components/KbdChip";
import { useInventoryShell } from "../../components/shell/InventoryShellContext";
import { InterchangeRuleExample } from "./InterchangeChainChips";
import { previewSteps } from "./interchangeRuleMath";
import { formatPreviewQty, type InterchangeMemberDraft } from "./interchangeablesShared";

// Примерната липса — в единиците на липсващия продукт. 10 е кръгло, за да се
// вижда пропорцията („10 кг A → 7 кг B" = 1 : 0.7).
const EXAMPLE_SHORTAGE = 10;

type Props = {
  members: InterchangeMemberDraft[];
  sortIndexes: (number | null)[];
  decimalsOf: (productId: number) => number;
  // пречките пред записа (празно = готово)
  issues: string[];
};

export const InterchangeRulePreview = ({ members, sortIndexes, decimalsOf, issues }: Props) => {
  const { t } = useTranslation();
  const { isNarrow } = useInventoryShell();
  const [selectedKey, setSelectedKey] = useState<number | null>(null);

  // Избраният липсващ продукт; изтрит/неизбран → първият
  const recipientIndex = Math.max(
    0,
    members.findIndex((member) => member.key === selectedKey),
  );
  const recipient = members[recipientIndex];
  const unitOf = (member: InterchangeMemberDraft) =>
    member.unit ?? t("inventoryPage.ichUnitFallback");
  const qtyOf = (member: InterchangeMemberDraft, quantity: number) =>
    `${formatPreviewQty(quantity, decimalsOf(member.productId))} ${unitOf(member)}`;

  const steps =
    members.length >= 2
      ? previewSteps(members, sortIndexes, recipientIndex, EXAMPLE_SHORTAGE)
      : [];
  const recipientReady = recipient?.amount != null && recipient.amount > 0;

  const status =
    issues.length === 0 ? (
      <div className="flex flex-wrap items-center gap-2 rounded-lg bg-ok-tint px-3 py-2 text-[12.5px] font-semibold leading-[normal] text-ok-deep">
        <CheckCircleFilled />
        {t("inventoryPage.ichReady")}
        {!isNarrow && <KbdChip>Ctrl+Enter</KbdChip>}
      </div>
    ) : (
      <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
        {issues.map((issue) => (
          <li
            key={issue}
            className="flex items-start gap-2 rounded-lg bg-warn-tint px-3 py-2 text-[12.5px] font-medium leading-[1.4] text-warn-ink"
          >
            <ExclamationCircleFilled className="mt-[3px] shrink-0" />
            {issue}
          </li>
        ))}
      </ul>
    );

  const scenario = recipient && (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-col gap-1.5">
        <span className="text-[10.5px] font-semibold uppercase leading-[normal] tracking-[0.08em] text-muted">
          {t("inventoryPage.ichPreviewPick")}
        </span>
        <div className="flex flex-wrap gap-1.5">
          {members.map((member, index) => {
            const pressed = index === recipientIndex;
            return (
              <button
                key={member.key}
                type="button"
                aria-pressed={pressed}
                onClick={() => setSelectedKey(member.key)}
                className={`max-w-full cursor-pointer truncate rounded-full border px-2.5 py-1 text-[12.5px] leading-[normal] outline-none focus-visible:shadow-[0_0_0_2px_var(--color-brand)] ${
                  pressed
                    ? "border-brand bg-brand-tint font-semibold text-brand-deep-ink"
                    : "border-shade-3 bg-surface text-body hover:border-shade-5"
                }`}
              >
                {member.productName}
              </button>
            );
          })}
        </div>
      </div>

      {recipientReady ? (
        <>
          <p className="m-0 text-[13px] leading-[1.45] text-body">
            {t("inventoryPage.ichPreviewShortage")}{" "}
            <b className="font-semibold text-ink">
              {qtyOf(recipient, EXAMPLE_SHORTAGE)} {recipient.productName}
            </b>
            {" — "}
            {t("inventoryPage.ichPreviewOffer")}
          </p>
          <ol className="m-0 flex list-none flex-col gap-2 p-0">
            {steps.map((step, stepIndex) => (
              <li key={step[0].member.key} className="flex items-start gap-2.5">
                <span
                  className={`mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full font-sheet text-[11px] font-bold leading-none ${
                    steps.length > 1 ? "bg-brand-tint text-brand-deep-ink" : "bg-fill-2 text-muted"
                  }`}
                >
                  {steps.length > 1 ? stepIndex + 1 : "→"}
                </span>
                <div className="min-w-0 flex-1 text-[13px] leading-[1.45] text-ink">
                  {step.map((donor, donorIndex) => (
                    <span key={donor.member.key}>
                      {donorIndex > 0 && (
                        <span className="text-muted"> {t("inventoryPage.ichPreviewOr")} </span>
                      )}
                      {donor.quantity != null ? (
                        <b className="font-sheet font-semibold">{qtyOf(donor.member, donor.quantity)}</b>
                      ) : (
                        <span className="text-warn-ink">{t("inventoryPage.ichPreviewNeedAmount")}</span>
                      )}{" "}
                      {donor.member.productName}
                    </span>
                  ))}
                  {step.length > 1 && (
                    <div className="text-xs leading-[1.4] text-muted">
                      {t("inventoryPage.ichPreviewOldestNote")}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
          {steps.length > 1 && (
            <p className="m-0 text-xs leading-[1.4] text-muted">{t("inventoryPage.ichPreviewNextNote")}</p>
          )}
        </>
      ) : (
        <p className="m-0 text-[12.5px] leading-[1.45] text-warn-ink">
          {t("inventoryPage.ichPreviewNeedRecipient", { name: recipient.productName })}
        </p>
      )}
    </div>
  );

  return (
    <section className="flex flex-col gap-3 rounded-xl border border-shade-2 bg-panel px-4 py-3.5">
      <h2 className="m-0 text-[15px] font-semibold leading-[1.2] text-ink">
        {t("inventoryPage.ichPreviewTitle")}
      </h2>
      {status}
      {members.length >= 2 ? scenario : <InterchangeRuleExample />}
      <p className="m-0 border-t border-shade-2 pt-2.5 text-xs leading-[1.45] text-muted">
        {t("inventoryPage.ichPreviewWhere")}
      </p>
    </section>
  );
};

export default InterchangeRulePreview;
